import React, { useCallback, useEffect, useState } from 'react';
import { Author } from './types/Author';
import AuthorAndTopicCard from '../newsletter/components/AuthorAndTopicCard';
import { subscribeToAuthorOrTopic, unsubscribeFromAuthorOrTopic } from '../newsletter/services/NewsletterService';
import { NewsletterVariant } from './types/Newsletter';
import { List } from 'newsletter/types/Newsletter';
import CustomSnackbar from '../components/CustomSnackbar';
import { useTranslations } from '../localization/useTranslations';
import { Term } from './types/Term';
import Dialog from '../common/components/Dialog';
import AuthorDetailsDialogContent from '../newsletter/components/AuthorDetailsDialogContent';
import { handleUserActionWithLoginCheck } from '../common/LoggedOutUserActions';
import useScrollbackAfterRedirect from '../hooks/useScrollbackAfterRedirect';

interface AuthorAndTopicBoxProps {
  cardsType: 'author' | 'topic';
  layout:
    | 'article-term'
    | 'topic-card'
    | 'in-modal'
    | 'article-top'
    | 'article-bottom'
    | 'followed-authors'
    | 'full-width'
    | 'carousel';
  selectedRole?: string;
  authors?: Author[];
}

const AuthorAndTopicBox: React.FC<AuthorAndTopicBoxProps> = ({ layout, cardsType, selectedRole, authors }) => {
  useScrollbackAfterRedirect();
  const [internalAuthors, setInternalAuthors] = useState<Author[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [availableAuthors, setAvailableAuthors] = useState<number[]>([]);
  const [subscribedVariants, setSubscribedVariants] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [terms, setTerms] = useState<Term[]>([]);
  const [singleAuthor, setSingleAuthor] = useState<Author | null>(null);
  const [openModalId, setOpenModalId] = useState<number | null>(null);

  const translations = useTranslations();

  // Function to open the author modal dialog
  const openAuthorModal = (id: number) => {
    setOpenModalId(id);
  };

  // Fetch available authors from the newsletter service
  const fetchAvailableAuthors = () => {
    const response = window.NewsletterLists;

    if (response.status === 'ok') {
      const ids: number[] = [];
      response.lists.forEach((list: List) => {
        Object.values(list.variants).forEach((variant: NewsletterVariant) => {
          ids.push(Number(variant.code));
        });
      });

      setAvailableAuthors(ids);

      const windowTerms = window.ArticleTerms;

      if (windowTerms && windowTerms.length > 0) {
        const filteredTerms = windowTerms.filter((term) => term?.term_id && ids.includes(term.term_id));
        setTerms(filteredTerms);
      }
    } else {
      console.error('Failed to fetch available authors');
    }
  };

  // useEffect hook to run once on component mount
  useEffect(() => {
    const windowAuthors = window.ArticleAuthors;
    const singleAuthor = window.singleAuthor;

    if (windowAuthors) {
      setInternalAuthors(windowAuthors);
    }

    if (singleAuthor) {
      setSingleAuthor(singleAuthor);
    }

    fetchAvailableAuthors();

    const user = window.crm_user;
    if (user) {
      const authorVariants = user.mail.find((mail) => mail.code === 'authors')?.variants ?? [];
      const topicVariants = user.mail.find((mail) => mail.code === 'topics')?.variants ?? [];
      const subscribedVariants = [...authorVariants, ...topicVariants];
      setIsLoggedIn(true);
      setSubscribedVariants(subscribedVariants);
    }
  }, []);

  // Function to open a snackbar with a given message
  const openSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  // Callback to check if an author or topic is subscribed
  const checkIfSubbed = useCallback(
    (id: number) => {
      return subscribedVariants.includes(id.toString());
    },
    [subscribedVariants]
  );

  // Handle subscription or unsubscription to an author or topic
  const handleSubscribe = useCallback(
    (id: number, type: 'authors' | 'topics') => async () => {
      if (!isLoggedIn) {
        if (openModalId === id) {
          setOpenModalId(null);
        }

        const userAction = type === 'authors' ? 'follow-author' : 'follow-topic';

        // check if user user is logged in. If not, set pending action details in session storage and exit
        if (!handleUserActionWithLoginCheck(userAction, id)) {
          return;
        }
      }

      const isSubscribed = checkIfSubbed(id);
      try {
        const response = isSubscribed
          ? await unsubscribeFromAuthorOrTopic(type, id)
          : await subscribeToAuthorOrTopic(type, id);

        if (response.status === 'ok') {
          const newSubscribedVariants = isSubscribed
            ? subscribedVariants.filter((variantId) => variantId !== id.toString())
            : [...subscribedVariants, id.toString()];

          setSubscribedVariants(newSubscribedVariants);

          window.dispatchEvent(
            new CustomEvent('subscribedVariantsChanged', {
              detail: newSubscribedVariants,
            })
          );

          let message;
          if (isSubscribed) {
            message =
              type === 'authors'
                ? translations?.snackBarMessages?.unsubscribedAuthor || 'Successfully unsubscribed from author'
                : translations?.snackBarMessages?.unsubscribedTopic || 'Successfully unsubscribed from topic';
          } else {
            message =
              type === 'authors'
                ? translations?.snackBarMessages?.subscribedAuthor || 'Successfully subscribed to author'
                : translations?.snackBarMessages?.subscribedTopic || 'Successfully subscribed to topic';
          }

          openSnackbar(message);
        }
      } catch (error) {
        console.error('There has been a problem with your fetch operation:', error);
        const message = isSubscribed
          ? translations?.snackBarMessages?.failedUnsubscribeAuthor || 'Error unsubscribing from author'
          : translations?.snackBarMessages?.failedSubscribeAuthor || 'Error subscribing to author';

        openSnackbar(message);
      }
    },
    [isLoggedIn, checkIfSubbed, translations, openModalId, subscribedVariants]
  );

  // Get URL for a given term
  const getTermUrl = (term: Term) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/${term.taxonomy === 'stitek' ? 'tag' : term.taxonomy}/${term.slug}`;
  };

  // useEffect hook to handle updates to subscribed variants
  useEffect(() => {
    const handleSubscribedVariantsChanged = (event: CustomEvent) => {
      setSubscribedVariants(event.detail);
    };

    window.addEventListener('subscribedVariantsChanged', handleSubscribedVariantsChanged);
    return () => window.removeEventListener('subscribedVariantsChanged', handleSubscribedVariantsChanged);
  }, []);

  // useEffect hook to display a snackbar message from local storage
  useEffect(() => {
    // Retrieve the snackbar message type from session storage
    const snackbarMessageType = localStorage.getItem('snackbarMessage');

    // Function to get the message based on the message type stored before login
    const getMessage = () => {
      switch (snackbarMessageType) {
        case 'follow-author-success':
          return translations?.snackBarMessages?.subscribedAuthor || 'Author followed successfully!';
        case 'follow-author-error':
          return translations?.snackBarMessages?.failedSubscribeAuthor || 'Error following author. Please try again.';
        case 'follow-topic-success':
          return translations?.snackBarMessages?.subscribedTopic || 'Topic followed successfully!';
        case 'follow-topic-error':
          return translations?.snackBarMessages?.failedSubscribeTopic || 'Error following topic. Please try again.';
        default:
          return '';
      }
    };

    // If a snackbar message is found, display it and remove it from local storage
    if (snackbarMessageType) {
      openSnackbar(getMessage());

      localStorage.removeItem('snackbarMessage');
    }
  }, [translations]);

  // Check if an author is followable
  const checkIfFollowable = (id: number) => {
    return availableAuthors.includes(id);
  };

  // Filter authors based on selected role
  const displayedAuthors = authors || internalAuthors;

  let filteredAuthors = authors?.length
    ? displayedAuthors.filter((author) => {
        return (
          selectedRole === 'all' ||
          (Array.isArray(author.role) ? author.role.includes(selectedRole) : author.role === selectedRole)
        );
      })
    : displayedAuthors;

  if (layout === 'article-bottom') {
    filteredAuthors = filteredAuthors.filter(({ term_id }) => {
      return checkIfFollowable(term_id);
    });
  }

  return (
    <div>
      <CustomSnackbar message={snackbarMessage} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />
      {cardsType === 'topic' ? (
        // Render topic cards
        terms.map((term) => (
          <AuthorAndTopicCard
            key={term.term_id}
            title={term.name}
            id={term.term_id.toString()}
            type={cardsType}
            isFollowed={checkIfSubbed(term.term_id)}
            imageUrl={term.image}
            name={term.name}
            link={getTermUrl(term)}
            shortDescription={term.description}
            onFollowClick={handleSubscribe(term.term_id, `${cardsType}s`)}
            isFollowable={checkIfFollowable(term.term_id)}
            layout={layout}
            nameType="link"
          />
        ))
      ) : filteredAuthors.length > 0 ? (
        // Render author cards
        filteredAuthors.map((author) => (
          <React.Fragment key={author.term_id}>
            <AuthorAndTopicCard
              title={author.name}
              id={author.term_id.toString()}
              type={cardsType}
              isFollowed={checkIfSubbed(author.term_id)}
              imageUrl={author.image}
              name={author.name}
              link={author.url || author.archive_link}
              description={author.description}
              shortDescription={author.position}
              onFollowClick={handleSubscribe(author.term_id, `${cardsType}s`)}
              isFollowable={availableAuthors.includes(author.term_id)}
              layout={layout}
              hasSocialLinks={layout === 'in-modal' || layout === 'article-bottom'}
              instagram={author.instagram}
              linkedin={author.linkedin}
              twitter={author.twitter}
              email={author.email}
              // temporary remove of author modal from single page
              nameType={
                layout !== 'article-bottom' && layout !== 'article-top'
                  ? checkIfFollowable(author.term_id)
                    ? 'button'
                    : 'link'
                  : 'link'
              }
              openAuthorModal={() => openAuthorModal(author.term_id)}
            />
            <Dialog isOpen={openModalId === author.term_id} onClose={() => setOpenModalId(null)}>
              <AuthorDetailsDialogContent
                id={author.term_id.toString()}
                authorName={author.name}
                authorImageUrl={author.image}
                authorTitle={author.name}
                authorDescription={author.description}
                authorLink={author.url}
                onFollowClick={handleSubscribe(author.term_id, `${cardsType}s`)}
                isFollowed={checkIfSubbed(author.term_id)}
                hasSocialLinks
                email={author.email}
                twitter={author.twitter}
                instagram={author.instagram}
                linkedin={author.linkedin}
                isFollowable={availableAuthors.includes(author.term_id)}
                onClose={() => {
                  setOpenModalId(null);
                }}
              />
            </Dialog>
          </React.Fragment>
        ))
      ) : singleAuthor ? (
        // Render single author card
        <AuthorAndTopicCard
          key={singleAuthor.term_id}
          title={singleAuthor.name}
          id={singleAuthor.term_id.toString()}
          type={cardsType}
          isFollowed={checkIfSubbed(singleAuthor.term_id)}
          imageUrl={singleAuthor.image}
          name={singleAuthor.name}
          link={singleAuthor.url || singleAuthor.archive_link}
          description={singleAuthor.description}
          shortDescription={singleAuthor.position}
          onFollowClick={handleSubscribe(singleAuthor.term_id, `${cardsType}s`)}
          isFollowable={availableAuthors.includes(singleAuthor.term_id)}
          layout="in-modal"
          hasSocialLinks={true}
          instagram={singleAuthor.instagram}
          linkedin={singleAuthor.linkedin}
          twitter={singleAuthor.twitter}
          email={singleAuthor.email}
          nameType="div" // on this page it should not be clickable
        />
      ) : null}
    </div>
  );
};

export default AuthorAndTopicBox;
